import React from "react";
import { Link } from "react-router-dom";
import { Star, ArrowRight, Phone, ArrowLeft } from "lucide-react";
import { SITE } from "../site.config";
import useDocumentMeta from "../hooks/useDocumentMeta";

export default function ReviewPage() {
  useDocumentMeta({
    title: `Leave a Google Review — ${SITE.business.name}`,
    description: `Share your experience with ${SITE.business.fullName}. Click the button to leave a quick Google review — it takes 30 seconds and helps us out massively.`,
    canonical: "https://powerzautoelectrical.com.au/review",
  });

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Background flair */}
      <div
        className="absolute -top-40 -right-40 w-[700px] h-[700px] rounded-full opacity-20 blur-3xl"
        style={{
          background: "radial-gradient(circle, #5EE6E6, transparent 70%)",
        }}
        aria-hidden="true"
      />
      <div
        className="absolute -bottom-40 -left-40 w-[700px] h-[700px] rounded-full opacity-10 blur-3xl"
        style={{
          background: "radial-gradient(circle, #5EE6E6, transparent 70%)",
        }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 grain" aria-hidden="true" />

      <div className="relative z-10 max-w-3xl mx-auto px-5 py-16 md:py-24 min-h-screen flex flex-col">
        <Link
          to="/"
          data-testid="review-back-home"
          className="inline-flex items-center gap-2 text-white/60 hover:text-cyan-pz transition-colors bebas tracking-widest text-sm mb-12"
        >
          <ArrowLeft className="w-4 h-4" />
          BACK TO HOME
        </Link>

        <div className="flex-1 flex flex-col justify-center">
          <div className="text-center">
            <img
              src={SITE.business.logo}
              alt={SITE.business.fullName}
              className="h-32 md:h-40 w-auto mx-auto mb-10 drop-glow-cyan"
            />

            <div className="inline-flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-cyan-pz" />
              <span className="bebas text-cyan-pz tracking-[0.3em] text-sm">
                CHEERS LEGEND
              </span>
              <span className="h-px w-10 bg-cyan-pz" />
            </div>

            <h1
              data-testid="review-headline"
              className="bebas text-white text-5xl md:text-7xl lg:text-8xl leading-[0.9] mb-6"
            >
              HOW WAS <br />
              <span className="text-cyan-pz glow-cyan">THE JOB?</span>
            </h1>

            <p className="text-white/70 barlow text-lg md:text-xl max-w-xl mx-auto leading-relaxed mb-10">
              If we did right by you, please drop a quick Google review. It
              takes 30 seconds and helps a heap of locals find us. We read every
              one.
            </p>

            <div className="flex justify-center gap-1 mb-10">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className="w-9 h-9 md:w-11 md:h-11 fill-cyan-pz text-cyan-pz drop-glow-cyan flicker"
                  style={{ animationDelay: `${i * 0.15}s` }}
                />
              ))}
            </div>

            <a
              href={SITE.business.googleReviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="leave-review-btn"
              className="inline-flex items-center justify-center gap-3 bg-cyan-pz text-black px-10 py-6 bebas text-2xl tracking-[0.2em] hover:bg-white transition-colors clip-perf pulse-cyan"
            >
              <GoogleGlyph />
              LEAVE A GOOGLE REVIEW
              <ArrowRight className="w-6 h-6" />
            </a>

            <p className="mt-6 text-white/40 barlow text-sm">
              Opens Google in a new tab — no login required if you're already
              signed in.
            </p>

            <div className="mt-16 pt-10 border-t border-white/10">
              <p className="bebas text-white/60 tracking-widest text-sm mb-4">
                NOT QUITE 5 STARS? TELL US DIRECTLY
              </p>
              <p className="text-white/70 barlow text-base mb-6">
                If something didn't sit right, give us a bell and we'll make
                it right — that's a promise.
              </p>
              <a
                href={`tel:${SITE.business.phoneTel}`}
                data-testid="review-call-btn"
                className="inline-flex items-center gap-2 border border-white/30 text-white px-7 py-4 bebas text-lg tracking-widest hover:border-cyan-pz hover:text-cyan-pz transition-colors"
              >
                <Phone className="w-4 h-4" />
                CALL {SITE.business.phone}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="24"
      height="24"
      aria-hidden="true"
      className="shrink-0"
    >
      <path
        fill="#000"
        d="M21.35 11.1H12v2.94h5.43c-.23 1.51-1.59 4.41-5.43 4.41-3.27 0-5.93-2.71-5.93-6.05s2.66-6.05 5.93-6.05c1.86 0 3.11.79 3.82 1.47l2.6-2.5C16.78 3.84 14.58 3 12 3 6.92 3 2.79 7.13 2.79 12.21S6.92 21.42 12 21.42c6.93 0 9.45-4.86 9.45-9.36 0-.63-.07-1.11-.1-.96Z"
      />
    </svg>
  );
}
